import { extractOriginalName } from '@/shared/utils/extract-original-name.util';
import { downloadFromSignedUrl } from '@/shared/utils/signed-url.util';
import { Dialog } from '@noorden-insurtech/alba-design-system';
import { Download, FileText, Loader2, MessageSquareText } from 'lucide-react';
import { useState } from 'react';

interface ImprovementLetterAttachment {
  key: string;
  url: string;
}

interface ImprovementLetterModalProps {
  isOpen: boolean;
  onClose: () => void;
  text?: string | null;
  attachments?: ImprovementLetterAttachment[];
}

export function ImprovementLetterModal({
  isOpen,
  onClose,
  text,
  attachments = [],
}: ImprovementLetterModalProps) {
  const [downloadingKey, setDownloadingKey] = useState<string | null>(null);

  const hasText = Boolean(text?.trim());

  async function handleDownload(attachment: ImprovementLetterAttachment) {
    try {
      setDownloadingKey(attachment.key);
      await downloadFromSignedUrl(
        attachment.url,
        extractOriginalName(attachment.key)
      );
    } catch (error) {
      console.error('Erro ao baixar anexo da carta de melhoria:', error);
    } finally {
      setDownloadingKey(null);
    }
  }

  return (
    <Dialog.Root open={isOpen} onOpenChange={() => onClose()}>
      <Dialog.Backdrop />
      <Dialog.Positioner>
        <Dialog.Content className='w-full max-w-md gap-2 pb-2'>
          <Dialog.Header className='flex flex-row items-center justify-between pb-2'>
            <Dialog.Title className='text-lg'>
              <MessageSquareText className='text-alba-gray-600 size-6' />
              Carta de Melhoria
            </Dialog.Title>
            <Dialog.CloseButton onClick={() => onClose()} />
          </Dialog.Header>

          <Dialog.Body className='flex flex-col gap-4 max-h-[60vh] overflow-y-auto pt-0'>
            <div className='flex flex-col gap-3 rounded-lg border border-alba-gray-400 p-4'>
              <p className='font-montserrat text-sm font-bold text-alba-black-500 border-b border-alba-gray-400 pb-3'>
                Sugestão de Melhorias
              </p>
              <p className='font-montserrat whitespace-pre-wrap text-sm text-alba-gray-600'>
                {hasText ? text : 'Nenhuma sugestão de melhoria registrada.'}
              </p>
            </div>

            {attachments.length > 0 && (
              <div className='flex flex-col gap-2'>
                <p className='font-montserrat text-xs font-bold text-alba-black-500'>
                  Anexos
                </p>

                {attachments.map(attachment => (
                  <button
                    key={attachment.key}
                    type='button'
                    onClick={() => handleDownload(attachment)}
                    disabled={downloadingKey === attachment.key}
                    className='flex flex-row items-center justify-start gap-3 rounded-lg border border-alba-gray-400 px-3 py-2 transition-colors hover:bg-alba-gray-50 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed'
                  >
                    <FileText className='text-alba-black-500 size-6 shrink-0' />

                    <div className='flex flex-1 flex-col items-start justify-start'>
                      <p className='font-montserrat text-sm text-alba-black-500 text-left break-all'>
                        {extractOriginalName(attachment.key)}
                      </p>
                      <p className='font-montserrat text-xs font-normal text-alba-gray-600'>
                        Anexo da carta de melhoria
                      </p>
                    </div>

                    {downloadingKey === attachment.key ? (
                      <Loader2 className='text-alba-blue-600 size-4 shrink-0 animate-spin' />
                    ) : (
                      <Download className='text-alba-blue-600 size-4 shrink-0' />
                    )}
                  </button>
                ))}
              </div>
            )}
          </Dialog.Body>
        </Dialog.Content>
      </Dialog.Positioner>
    </Dialog.Root>
  );
}
